import { ReactNode } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import PdfIcon from "@covertree/ui/icons/PdfIcon";
import { IconButton } from "../../buttons";

interface DocumentAction {
  name: string;
  icon?: ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  onClick(): void;
}

interface Props {
  name: string;
  description?: string;
  actions?: DocumentAction[];
}

export const DocumentItem = ({ actions, name, description }: Props) => {
  return (
    <Box
      p={3}
      border="1px solid"
      borderRadius={1}
      borderColor="action.disabled"
      mb={1}>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        justifyContent="space-between">
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box
            width={64}
            height={64}
            borderRadius="8px"
            bgcolor="secondary.contrastText"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              flexBasis: 64,
              flexGrow: 0,
              flexShrink: 0,
            }}>
            <PdfIcon />
          </Box>
          <Stack direction="column">
            <Typography
              sx={{
                maxWidth: {
                  xs: 120,
                  md: 500,
                },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textWrap: "wrap",
              }}>
              {name}
            </Typography>
            {!!description && (
              <Typography variant="subtitle1">{description}</Typography>
            )}
          </Stack>
        </Stack>
        <Box display="flex" flexDirection="row">
          {actions?.map(({ isLoading, disabled, onClick, icon, name }) => (
            <Box key={name} position="relative" display="inline-block" ml={1}>
              {isLoading && (
                <Box
                  width={24}
                  height={24}
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  margin="auto">
                  <CircularProgress color="primary" size={24} />
                </Box>
              )}
              {icon ? (
                <IconButton
                  onClick={onClick}
                  disabled={isLoading || disabled}
                  sx={(theme) => ({
                    ...(isLoading || disabled ? { opacity: 0.3 } : {}),
                    width: theme.spacing(5),
                    height: theme.spacing(5),
                    border: `1px solid ${theme.palette.grey[200]}`,
                  })}
                  data-testid="download-icon-button">
                  {icon}
                </IconButton>
              ) : (
                <Button
                  sx={{
                    ...(isLoading || disabled ? { opacity: 0.3 } : {}),
                    padding: {
                      xs: 0,
                      md: 4,
                    },
                  }}
                  variant="text"
                  onClick={onClick}
                  disabled={isLoading || disabled}>
                  {name}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>
  );
};

export default DocumentItem;
